import { useEffect, useRef, useState, type MouseEvent } from "react"
import type { JSONContent } from "@tiptap/core"
import { UnderlineOutlined } from "@ant-design/icons"
import { Button, Flex, Input, Popover, Typography, message } from "antd"
import {
  getConfigEditorDocument,
  getConfigResources,
  getPromptVariableNameError,
  updateConfigPromptDocument,
} from "~/common/videoTemplate/config"
import type { VideoTemplate } from "~/common/videoTemplate/types"
import { AssetPromptInput } from "~/components/AssetPromptInput"
import type {
  AssetPromptInputRef,
  PromptSelection,
} from "~/components/AssetPromptInput/types"
import styles from "./index.css"

type PromptEditorProps = {
  config: VideoTemplate
  onChange: (config: VideoTemplate) => void
}

// 菜单位置相对编辑器容器；hasField 表示选区落在变量框内或包含变量框
type MenuPosition = { left: number; top: number; hasField: boolean }

const getFieldsKey = (config: VideoTemplate) => JSON.stringify(config.fields)

// 阻止按钮抢走焦点，保留编辑器中的选区
const keepSelection = (event: MouseEvent) => event.preventDefault()

/**
 * 所见即所得的模板提示词编辑器：引用、变量框、下划线与用户侧展示一致。
 * 编辑时以编辑器文档为准，只有提示词被外部改动（如删除素材后重排编号）才重建文档。
 */
export const PromptEditor = ({ config, onChange }: PromptEditorProps) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const editorRef = useRef<AssetPromptInputRef>(null)
  const [document, setDocument] = useState<JSONContent>(() =>
    getConfigEditorDocument(config),
  )
  const synced = useRef({ prompt: config.prompt, fields: getFieldsKey(config) })
  const [menu, setMenu] = useState<MenuPosition | null>(null)
  // null 表示操作菜单，字符串表示正在输入变量名
  const [variableName, setVariableName] = useState<string | null>(null)
  const resources = getConfigResources(config)

  useEffect(() => {
    const fields = getFieldsKey(config)
    if (
      config.prompt === synced.current.prompt &&
      fields === synced.current.fields
    )
      return
    synced.current = { prompt: config.prompt, fields }
    setDocument(getConfigEditorDocument(config))
  }, [config])

  const closeMenu = () => {
    setMenu(null)
    setVariableName(null)
  }

  const handleDocumentChange = (next: JSONContent) => {
    setDocument(next)
    const updated = updateConfigPromptDocument(config, next)
    const fields = getFieldsKey(updated)
    synced.current = { prompt: updated.prompt, fields }
    // 编辑器初始化等不改变内容的通知不触发修改，避免误判为未保存
    if (updated.prompt !== config.prompt || fields !== getFieldsKey(config))
      onChange(updated)
  }

  const handleSelectionChange = (selection: PromptSelection | null) => {
    const container = containerRef.current
    if (!selection || !selection.text.trim() || !container) {
      // 正在输入变量名时编辑器失焦，不收起菜单
      if (variableName === null) closeMenu()
      return
    }
    const rect = container.getBoundingClientRect()
    setVariableName(null)
    setMenu({
      left: selection.rect.left - rect.left,
      top: selection.rect.top - rect.top,
      hasField: selection.hasField,
    })
  }

  const setUnderline = (underline: boolean) => {
    editorRef.current?.setSelectionUnderline(underline)
    closeMenu()
  }

  const unwrapVariable = () => {
    editorRef.current?.unwrapSelectionFields()
    closeMenu()
  }

  const variableError =
    variableName === null ? "" : getPromptVariableNameError(variableName)
  const confirmVariable = () => {
    if (variableName === null || variableError) return
    const error = editorRef.current?.wrapSelectionAsField(variableName) ?? ""
    closeMenu()
    if (error) void message.warning(error)
  }

  const menuContent =
    variableName === null ? (
      <Flex gap={4}>
        <Button
          size="small"
          type="text"
          icon={<UnderlineOutlined />}
          onMouseDown={keepSelection}
          onClick={() => setUnderline(true)}
        >
          加下划线
        </Button>
        <Button
          size="small"
          type="text"
          onMouseDown={keepSelection}
          onClick={() => setUnderline(false)}
        >
          取消下划线
        </Button>
        {menu?.hasField ? (
          <Button
            size="small"
            type="text"
            onMouseDown={keepSelection}
            onClick={unwrapVariable}
          >
            取消变量
          </Button>
        ) : (
          <Button
            size="small"
            type="text"
            onMouseDown={keepSelection}
            onClick={() => setVariableName("")}
          >
            设为变量
          </Button>
        )}
      </Flex>
    ) : (
      <Flex
        className={styles.variableForm}
        vertical
        gap={6}
      >
        <Flex gap={8}>
          <Input
            size="small"
            autoFocus
            aria-label="变量名"
            placeholder="变量名，如：核心卖点"
            status={variableName && variableError ? "error" : undefined}
            value={variableName}
            onChange={(event) => setVariableName(event.target.value)}
            onPressEnter={confirmVariable}
          />
          <Button
            size="small"
            type="primary"
            disabled={Boolean(variableError)}
            onMouseDown={keepSelection}
            onClick={confirmVariable}
          >
            确定
          </Button>
        </Flex>
        <Typography.Text
          type={variableName && variableError ? "danger" : "secondary"}
          className={styles.variableHint}
        >
          {variableName && variableError
            ? variableError
            : "选中的文字会作为变量的初始内容，可在变量框内修改"}
        </Typography.Text>
      </Flex>
    )

  return (
    <div
      ref={containerRef}
      className={styles.promptEditor}
    >
      <AssetPromptInput
        ref={editorRef}
        className={styles.promptInput}
        placeholder="输入模板提示词，输入 @ 引用参考素材"
        mentionableResources={resources}
        document={document}
        // Staff 可直接删除变量框（连同内容），用户侧仍会自动补回
        restoreDeletedFields={false}
        onDocumentChange={handleDocumentChange}
        onSelectionChange={handleSelectionChange}
      />
      <Popover
        open={Boolean(menu)}
        trigger="click"
        placement="top"
        content={menuContent}
        onOpenChange={(open) => {
          if (!open) closeMenu()
        }}
      >
        <span
          className={styles.selectionAnchor}
          style={{ left: menu?.left ?? 0, top: menu?.top ?? 0 }}
        />
      </Popover>
    </div>
  )
}
